import type {
	PortalCatalogConfigResponseDto,
	PortalCatalogFacetedQueryDto,
	PortalCatalogFacetedResponseDto,
	PortalCatalogItemDetailResponseDto,
	PortalCatalogQueryDto,
	PortalCatalogResponseDto,
	PortalCategoriesResponseDto,
} from '@supply-mover-2-0/shared';
import { buildQueryString, portalFetch } from '../lib/apiClient';

/**
 * Fetch catalog configuration (available filters and sort options for this tenant).
 */
export async function getCatalogConfig(
	token: string,
): Promise<PortalCatalogConfigResponseDto> {
	return portalFetch<PortalCatalogConfigResponseDto>('/catalog/config', token);
}

/**
 * Search the product catalog visible to the current portal user's active client.
 */
export async function searchCatalog(
	token: string,
	params: PortalCatalogQueryDto,
): Promise<PortalCatalogResponseDto> {
	const qs = buildQueryString({
		search: params.search,
		category: params.category,
		subcategory: params.subcategory,
		manufacturer: params.manufacturer,
		page: params.page,
		limit: params.limit,
		sortBy: params.sortBy,
		sortOrder: params.sortOrder,
		isInStock: params.isInStock,
		isShortDate: params.isShortDate,
		isFavorite: params.isFavorite,
		isPreviouslyPurchased: params.isPreviouslyPurchased,
		isOnOrder: params.isOnOrder,
	});
	return portalFetch<PortalCatalogResponseDto>(`/catalog/items${qs}`, token);
}

/**
 * Faceted catalog search — filters + pagination + live facet counts in one
 * call. POST because the filter payload (multi-value terms, ranges, custom
 * fields) doesn't flatten into query params; same optional-auth semantics as
 * `searchCatalog`. Response `data`/`pagination` match the legacy shape, plus
 * a `facets` block for the filter rail.
 */
export async function facetedSearchCatalog(
	token: string,
	body: PortalCatalogFacetedQueryDto,
): Promise<PortalCatalogFacetedResponseDto> {
	return portalFetch<PortalCatalogFacetedResponseDto>(
		'/catalog/items/faceted',
		token,
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		},
	);
}

/**
 * Fetch a single catalog item by ID.
 */
export async function getCatalogItem(
	token: string,
	itemId: number,
): Promise<PortalCatalogItemDetailResponseDto> {
	return portalFetch<PortalCatalogItemDetailResponseDto>(
		`/catalog/items/${itemId}`,
		token,
	);
}

/**
 * Fetch the category tree for catalog navigation.
 */
export async function getCategories(
	token: string,
): Promise<PortalCategoriesResponseDto> {
	return portalFetch<PortalCategoriesResponseDto>('/catalog/categories', token);
}

/**
 * Fetch the user's favorite items.
 */
export async function getFavorites(
	token: string,
): Promise<PortalCatalogResponseDto> {
	return portalFetch<PortalCatalogResponseDto>('/catalog/favorites', token);
}

/**
 * Add an item to the user's favorites.
 */
export async function addFavorite(
	token: string,
	itemId: number,
): Promise<{ success: boolean }> {
	return portalFetch<{ success: boolean }>(
		`/catalog/favorites/${itemId}`,
		token,
		{ method: 'POST' },
	);
}

/**
 * Remove an item from the user's favorites.
 */
export async function removeFavorite(
	token: string,
	itemId: number,
): Promise<{ success: boolean }> {
	return portalFetch<{ success: boolean }>(
		`/catalog/favorites/${itemId}`,
		token,
		{ method: 'DELETE' },
	);
}
